import React from 'react'
import { motion } from 'framer-motion'
import { ChevronDown, ArrowRight } from 'lucide-react'
import { scrollToSection } from '../lib/utils'

const Hero: React.FC = () => {
  const scrollToAbout = () => scrollToSection('about')

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-tech-900 via-tech-800 to-cyber-900 px-4">
      {/* Background Tech Pattern */}
      <div className="absolute inset-0 tech-pattern-bg opacity-10"></div>
      
      {/* Subtle Sci-Fi Background Effects */}
      <div className="absolute inset-0 overflow-hidden">

        
        {/* Subtle Corner Glows */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-cyber-500/10 to-transparent rounded-br-full"></div>
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-neon-500/10 to-transparent rounded-bl-full"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-cyber-400/10 to-transparent rounded-tr-full"></div>
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-neon-400/10 to-transparent rounded-tl-full"></div>
        
        {/* Floating Data Points */}
        <div className="absolute top-16 left-16 w-2 h-2 bg-cyber-400 rounded-full opacity-60 animate-ping" style={{ animationDuration: '3s' }}></div>
        <div className="absolute top-32 right-24 w-1.5 h-1.5 bg-neon-400 rounded-full opacity-60 animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-32 w-1 h-1 bg-cyber-300 rounded-full opacity-60 animate-ping" style={{ animationDuration: '5s', animationDelay: '2s' }}></div>
        <div className="absolute bottom-16 right-16 w-1.5 h-1.5 bg-neon-300 rounded-full opacity-60 animate-ping" style={{ animationDuration: '3.5s', animationDelay: '3s' }}></div>
      </div>



      <div className="container-custom text-center relative z-10 px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto flex flex-col items-center justify-center min-h-screen"
        >


          {/* Greeting */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-cyber-400 font-medium mb-4"
          >
            Hello, I'm
          </motion.p>

          {/* Name */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6"
          >
            <span className="gradient-text">Rishi Malhotra</span>
          </motion.h1>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-2xl md:text-3xl lg:text-4xl text-cyber-300 font-light mb-8"
          >
            Software Project Manager & Operations Head
          </motion.h2>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-lg md:text-xl text-cyber-200 mb-12 max-w-3xl mx-auto leading-relaxed"
          >
            Bridging strategy with execution through hands-on technology leadership. 
            Expert in mobile apps, web platforms, and AI-driven solutions with a focus on 
            building cross-functional teams and delivering scalable outcomes.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          >
            <motion.button
              onClick={() => scrollToSection('contact')}
              className="btn-primary group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Let's Connect
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-200" />
            </motion.button>
            
            <motion.button
              onClick={() => scrollToSection('projects')}
              className="btn-secondary group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View My Work
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-200" />
            </motion.button>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          >
            <motion.button
              onClick={scrollToAbout}
              className="p-2 rounded-full bg-cyber-500/20 backdrop-blur-sm border border-cyber-500/30 hover:bg-cyber-500/30 transition-colors duration-200 glow-effect"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <ChevronDown className="w-6 h-6 text-cyber-300" />
              </motion.div>
            </motion.button>
          </motion.div>
        </motion.div>
      </div>

      {/* Floating Elements */}
      <motion.div
        className="absolute top-1/4 right-10 hidden lg:block"
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-4 h-4 bg-cyber-500 rounded-full opacity-60 glow-effect"></div>
      </motion.div>
      
      <motion.div
        className="absolute bottom-1/4 left-10 hidden lg:block"
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-3 h-3 bg-neon-500 rounded-full opacity-60 neon-glow"></div>
      </motion.div>
    </section>
  )
}

export default Hero
